import React, { useState, useEffect, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Header from './components/Header';
import Balance from './components/Balance';
import TransactionList from './components/TransactionList';
import AddTransaction from './components/AddTransaction';
import Footer from './components/Footer';
import Notifications, { addToast } from './components/Notifications';

const API = 'https://piggybank-cors.andresnicolas.com/routes';

function App() {
  const [transactions, setTransactions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [username, setUsername] = useState(() => localStorage.getItem('username') || null);
  const [fabType, setFabType] = useState(null);
  const [showFabMenu, setShowFabMenu] = useState(false);

  const isAuthenticated = Boolean(username);

  useEffect(() => {
    if (fabType) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [fabType]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTransactions();
    } else {
      const saved = localStorage.getItem('transactions');
      setTransactions(saved ? JSON.parse(saved) : []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem('transactions', JSON.stringify(transactions));
    }
  }, [transactions, isAuthenticated]);

  const fetchTransactions = async () => {
    try {
      const res  = await fetch(`${API}/transactions.php`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setTransactions(data.transactions);
      }
    } catch {
      // silently fail when not authenticated or server unreachable
    }
  };

  const isFiltered = Boolean(startDate || endDate);

  const filteredTransactions = useMemo(() => {
    if (!isFiltered) return transactions;
    return transactions.filter(t => {
      const d     = new Date(t.date);
      const start = startDate ? new Date(startDate) : null;
      const end   = endDate ? new Date(endDate) : null;
      if (start && end) return d >= start && d <= end;
      if (start) return d >= start;
      if (end) return d <= end;
      return true;
    });
  }, [transactions, startDate, endDate, isFiltered]);

  const addTransaction = async (transaction) => {
    try {
      if (isAuthenticated) {
        const res  = await fetch(`${API}/transactions.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(transaction),
        });
        const data = await res.json();
        if (!data.success) { addToast('Failed to add transaction.', 'error'); return; }
        setTransactions(prev => [{ ...transaction, id: data.id, createdAt: new Date().toISOString() }, ...prev]);
      } else {
        setTransactions(prev => [{ ...transaction, id: Date.now(), createdAt: new Date().toISOString() }, ...prev]);
      }
      addToast('Transaction added.', 'success');
      setFabType(null);
      setShowFabMenu(false);
    } catch {
      addToast('Could not connect to server.', 'error');
    }
  };

  const deleteTransaction = async (id) => {
    try {
      if (isAuthenticated) {
        await fetch(`${API}/transactions.php`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ id }),
        });
      }
      setTransactions(prev => prev.filter(t => t.id !== id));
      addToast('Transaction deleted.', 'success');
    } catch {
      addToast('Could not delete transaction.', 'error');
    }
  };

  const updateTransaction = async (id, updatedTransaction) => {
    try {
      if (isAuthenticated) {
        await fetch(`${API}/transactions.php`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ id, ...updatedTransaction }),
        });
      }
      setTransactions(prev =>
        prev.map(t => t.id === id ? { ...updatedTransaction, id, updatedAt: new Date().toISOString() } : t)
      );
      addToast('Transaction updated.', 'success');
    } catch {
      addToast('Could not update transaction.', 'error');
    }
  };

  const importTransactions = async (imported, onResult) => {
    try {
      if (isAuthenticated) {
        const key = t => `${t.description.trim().toLowerCase()}|${t.date}|${parseFloat(t.amount).toFixed(2)}`;
        const existingKeys = new Set(transactions.map(key));
        const newOnes    = imported.filter(t => !existingKeys.has(key(t)));
        const duplicates = imported.length - newOnes.length;

        const results = await Promise.all(newOnes.map(t =>
          fetch(`${API}/transactions.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(t),
          }).then(r => r.json())
        ));

        const added = results.filter(r => r.success).map((r, i) => ({
          ...newOnes[i], id: r.id, createdAt: new Date().toISOString()
        }));

        setTransactions(prev => [...added, ...prev]);
        if (onResult) onResult({ added: added.length, duplicates });
      } else {
        setTransactions(prev => {
          const key = t => `${t.description.trim().toLowerCase()}|${t.date}|${parseFloat(t.amount).toFixed(2)}`;
          const existingKeys = new Set(prev.map(key));
          const newOnes    = imported.filter(t => !existingKeys.has(key(t)));
          const duplicates = imported.length - newOnes.length;
          if (onResult) onResult({ added: newOnes.length, duplicates });
          return [...newOnes, ...prev];
        });
      }
    } catch {
      addToast('Could not import transactions.', 'error');
    }
  };

  const handleLogin = (name) => setUsername(name);

  const handleLogout = () => {
    setUsername(null);
    const saved = localStorage.getItem('transactions');
    setTransactions(saved ? JSON.parse(saved) : []);
  };

  return (
    <div className="App">
      <div className="container py-4">
        <Header onLogin={handleLogin} onLogout={handleLogout} />
        <div className="row">
          <div className="col-md-8 mx-auto">
            <Balance
              transactions={transactions}
              filteredTransactions={filteredTransactions}
              isFiltered={isFiltered}
            />
            <div className="row">
              <div className="col-md-7">
                <TransactionList
                  transactions={transactions}
                  filteredTransactions={filteredTransactions}
                  startDate={startDate}
                  endDate={endDate}
                  onStartDate={setStartDate}
                  onEndDate={setEndDate}
                  onDelete={deleteTransaction}
                  onUpdate={updateTransaction}
                  onImport={importTransactions}
                />
              </div>
              <div className="col-md-5 desktop-only">
                <AddTransaction onAdd={addTransaction} />
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <button
        className={`fab ${showFabMenu ? 'open' : ''}`}
        onClick={() => setShowFabMenu(!showFabMenu)}
      >
        +
      </button>

      {showFabMenu && (
        <div className="fab-menu">
          <button
            className="fab-menu-btn income"
            onClick={() => { setFabType('income'); setShowFabMenu(false); }}
          >
            Income
          </button>
          <button
            className="fab-menu-btn expense"
            onClick={() => { setFabType('expense'); setShowFabMenu(false); }}
          >
            Expense
          </button>
        </div>
      )}

      {fabType && (
        <div className="fab-modal-overlay" onClick={() => setFabType(null)}>
          <div className="fab-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="fab-modal-handle" />
            <AddTransaction onAdd={addTransaction} initialType={fabType} />
          </div>
        </div>
      )}

      <Notifications />
    </div>
  );
}

export default App;